import type { BookingView, CurrentSession, MaintenanceList } from '@elite-residence/api-client';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useApiClient } from '../api/ApiClientProvider';
import { businessDate } from '../dates';
import { loadPendingFinance } from '../finance/loadPendingFinance';
import { type Dictionary, useText } from '../i18n/LocaleProvider';
import { Loading, QueryFailure } from '../ui/ui';
import { classifyDay } from './daily';
import { loadBookings } from './loadBookings';

type Booking = BookingView['booking'];
type Maintenance = MaintenanceList['maintenance'][number];

const text: Dictionary<
  | 'title'
  | 'today'
  | 'arrivals'
  | 'unsigned'
  | 'departures'
  | 'overstay'
  | 'maintenance'
  | 'pending'
  | 'empty'
  | 'failed'
> = {
  en: {
    title: 'Today',
    today: 'Business date',
    arrivals: 'Arrivals',
    unsigned: 'Unsigned arrivals',
    departures: 'Departure today',
    overstay: 'Overstay',
    maintenance: 'Maintenance overdue',
    pending: 'Pending payments',
    empty: 'None',
    failed: 'The operations lists could not be loaded.',
  },
  ar: {
    title: 'اليوم',
    today: 'تاريخ العمل',
    arrivals: 'الوصول',
    unsigned: 'وصول بدون توقيع',
    departures: 'مغادرة اليوم',
    overstay: 'تجاوز المدة',
    maintenance: 'صيانة متأخرة',
    pending: 'مدفوعات معلقة',
    empty: 'لا يوجد',
    failed: 'تعذر تحميل قوائم التشغيل.',
  },
};

const has = (session: CurrentSession, key: string) => session.permissions.includes(key);

export function OperationsPage({ session }: { readonly session: CurrentSession }) {
  const client = useApiClient();
  const t = useText(text);
  const today = businessDate(new Date());
  const canBook = has(session, 'booking.view');
  const canFinance = has(session, 'finance.booking_view');
  const canMaintain = [
    'maintenance.create_edit',
    'maintenance.planning',
    'maintenance.close',
    'maintenance.cancel_planned',
  ].some((key) => has(session, key));
  const bookings = useQuery({
    queryKey: ['operations', 'bookings'],
    enabled: canBook,
    queryFn: async ({ signal }) => {
      return loadBookings(client, {}, signal);
    },
  });
  const pending = useQuery({
    queryKey: ['operations', 'pending'],
    enabled: canFinance,
    queryFn: async ({ signal }) => {
      return loadPendingFinance(client, signal);
    },
  });
  const maintenance = useQuery({
    queryKey: ['operations', 'maintenance'],
    enabled: canMaintain,
    queryFn: async ({ signal }) => {
      const response = await client.listMaintenance({ query: {} }, { signal });
      if (response.status !== 200) throw new Error('maintenance_unavailable');
      return response.body.maintenance;
    },
  });
  if ((canBook && bookings.isPending) || (canMaintain && maintenance.isPending) || (canFinance && pending.isPending)) {
    return <Loading />;
  }
  if (bookings.isError || maintenance.isError || pending.isError) {
    return (
      <main>
        <h1>{t.title}</h1>
        <QueryFailure query={bookings} />
        <QueryFailure query={maintenance} />
        <QueryFailure query={pending} />
      </main>
    );
  }
  const day = classifyDay((bookings.data ?? []) as Booking[], (maintenance.data ?? []) as Maintenance[], today);
  const sections = [
    {
      title: t.arrivals,
      rows: day.arrivals.map((booking) => ({
        id: booking.id,
        label: booking.displayReference,
        to: `/bookings/${booking.id}`,
      })),
    },
    {
      title: t.unsigned,
      rows: day.unsignedArrivals.map((booking) => ({
        id: booking.id,
        label: booking.displayReference,
        to: `/bookings/${booking.id}`,
      })),
    },
    {
      title: t.departures,
      rows: day.departures.map((booking) => ({
        id: booking.id,
        label: booking.displayReference,
        to: `/bookings/${booking.id}`,
      })),
    },
    {
      title: t.overstay,
      rows: day.overstay.map((booking) => ({
        id: booking.id,
        label: booking.displayReference,
        to: `/bookings/${booking.id}`,
      })),
    },
    {
      title: t.maintenance,
      rows: day.maintenanceOverdue.map((item) => ({ id: `m:${item.id}`, label: item.unitNumber, to: '/maintenance' })),
    },
    {
      title: t.pending,
      rows: (pending.data ?? []).map((item) => ({
        id: item.transactionId,
        label: `${item.displayReference} ${item.amount}`,
        to: `/finance/tasks/${item.transactionId}`,
      })),
    },
  ];
  return (
    <main>
      <h1>{t.title}</h1>
      <p>
        {t.today}: <span className="er-mono">{today}</span>
      </p>
      {sections.map((section) => (
        <section className="er-card" key={section.title}>
          <h2>{section.title}</h2>
          {section.rows.length === 0 ? (
            <p>{t.empty}</p>
          ) : (
            <ul>
              {section.rows.map((row) => (
                <li key={row.id}>
                  <Link to={row.to}>{row.label}</Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </main>
  );
}
